import React, { useState } from "react";
import { useForm, FormProvider, Controller } from "react-hook-form";
import "./ListingDetails.css";

import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import InputField from "../../form-fields/InputField";
import SelectField from "../../form-fields/SelectField";
import { GetImages } from "../../../utils/GetImages";
import Card from "../../card";
import SimpleButton from "../../buttons/Simple-button";
import RightSideModal from "../../Modal/right-side-modal";
import Uploader from "../../CustomUploader/Uploader";
import Toggle from "../../form-fields/toggle";


interface ListingFormValues {
  isListed: boolean;
  rent: string;
  template: string;
  description: string;
}

interface ImageUploadValues {
  propertyImage: any;
  imageTitle: string;
  isMainImage: boolean;
}

const ListingDetails = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<any[]>([]);
  const [resetKey, setResetKey] = useState(0); 
  const uploadFormId = "upload-image-form";

  const mainMethods = useForm<ListingFormValues>({
    defaultValues: { isListed: false, rent: "200", template: "", description: "" },
  });

  const modalMethods = useForm<ImageUploadValues>({
    defaultValues: { propertyImage: null, imageTitle: "", isMainImage: false },
  });

  const handleDeleteImage = (id: number) => {
    setUploadedImages(prev => prev.filter(img => img.id !== id));
  };

  const handleReplaceImage = (id: number) => {
    handleDeleteImage(id);
    setIsModalOpen(true);
  };

  const onImageUploadSubmit = (data: ImageUploadValues) => {
    if (data.propertyImage) {
      const newImg = {
        id: Date.now(),
        label: data.imageTitle || "Untitled",
        isMain: data.isMainImage,
        url: URL.createObjectURL(data.propertyImage),
      };

      const updatedList = data.isMainImage 
        ? uploadedImages.map(img => ({ ...img, isMain: false })).concat(newImg)
        : [...uploadedImages, newImg];

      setUploadedImages(updatedList);
      
      modalMethods.reset();
      setResetKey(prev => prev + 1);
      setIsModalOpen(false);
    }
  };

  return (
    <Card className="mt-3">
      <div className="listing-container container-fluid">
        <FormProvider {...mainMethods}>
          <form onSubmit={mainMethods.handleSubmit(data => console.log(data))}>
            
            <div className="top-listing-header-top d-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-bold mb-0">List Unit</h5>
              {/* Header Toggle Integration */}
              <Controller
                name="isListed"
                control={mainMethods.control}
                render={({ field }) => (
                  <Toggle isChecked={field.value} onChange={field.onChange} />
                )}
              />
            </div>

            <div className="listing-basic-details">
              <div className="row">
                  <div className="col-md-2">
                      <InputField name="rent" label="Rent" required type="number" />
                  </div>
                  <div className="col-md-4">
                      <SelectField name="template" label="Lease Application Template" required options={[]} />
                  </div>
                  <div className="col-md-12 mt-3">
                      <label className="form-label fw-600">Description <span className="text-danger">*</span></label>
                      <Controller
                          name="description"
                          control={mainMethods.control}
                          render={({ field }) => <ReactQuill theme="snow" {...field} />}
                      />
                  </div>
              </div>
            </div>

            <div className="listing-images-details mt-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-bold mb-0">Property Images</h5>
                <SimpleButton label="Upload Images" onClick={() => {
                  setResetKey(prev => prev + 1);
                  setIsModalOpen(true);
                }} />
              </div>

              {uploadedImages.length === 0 ? (
                <div className="no-images-placeholder text-center py-5 border rounded">
                  <img src={GetImages.emptyStateData} alt="Empty" width="48" />
                  <p className="black fw-bold mt-2">No Images Uploaded</p>
                </div>
              ) : (
                <div className="images-grid-scroll">
                  <div className="d-flex gap-3 pb-2">
                    {uploadedImages.map((img) => (
                      <div key={img.id} className="image-card-wrapper">
                        <div className="image-card">
                          <img className="listing-images" src={img.url} alt={img.label} />
                          {img.isMain && <span className="main-star">★</span>}
                          <div className="image-actions">
                             <button type="button" className="action-btn" onClick={() => handleReplaceImage(img.id)}>
                               <img src={GetImages.BluePencilEdit} width="14" alt="replace" />
                             </button>
                             <button type="button" className="action-btn" onClick={() => handleDeleteImage(img.id)}>
                               <img src={GetImages.deleteIconRed} width="14" alt="delete" />
                             </button>
                          </div>
                          <div className="image-label">{img.label}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="d-flex justify-content-end gap-3 align-items-center mt-4 border-top pt-3">
               <button type="button" className="btn btn-link text-decoration-none text-dark fw-bold">Cancel</button>
               <SimpleButton label="Save Changes" type="submit" />
            </div>
          </form>
        </FormProvider>

        <RightSideModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title="Upload Property Image"
          buttonlabel="Save"
          formId={uploadFormId}
          maxWidth={480}
        >
          <FormProvider {...modalMethods}>
            <form id={uploadFormId} onSubmit={modalMethods.handleSubmit(onImageUploadSubmit)}>
              <div className="p-4">
                <div className="mb-4">
                  <Uploader 
                      name="propertyImage" 
                      maxSizeMB={1} 
                      required 
                      resetTrigger={resetKey} 
                  />
                </div>
                {/* Modal Toggle Integration */}
                <div className="d-flex justify-content-between align-items-center mb-4 p-2">
                  <span className="">Set as main image</span>
                  <Controller
                    name="isMainImage"
                    control={modalMethods.control}
                    render={({ field }) => (
                      <Toggle isChecked={field.value} onChange={field.onChange} />
                    )}
                  />
                </div>
                <InputField name="imageTitle" label="Image Title" placeholder="e.g. Master Bedroom" />
              </div>
            </form>
          </FormProvider>
        </RightSideModal>
      </div>
    </Card>
  );
};

export default ListingDetails;